import {
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useState, useRef } from "react";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useTheme } from "@mui/material/styles";
import {
  createUploadLawsuit,
  deleteUploadLawsuit,
  previewUploadLawsuitLawyers,
} from "../../services/LawsuitService";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import Tooltip from "@mui/material/Tooltip";
import CardHeader from "../General/CardHeader";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import FileUploadBox from "../General/FileUploadBox";
import GlassCard from "../General/GlassCard";
import OnlineQueryTooltip, {
  useOnlineQueryGate,
} from "../General/OnlineQueryGate";
import { useToast } from "../../context/ToastContext";
import LawyerSessionRequiredDialog from "../General/Dialog/LawyerSessionRequiredDialog";
import useLawyerSessionQueue from "../../hooks/useLawyerSessionQueue";

export default function Chargelawsuit() {
  const { showToast } = useToast();
  const { online } = useOnlineQueryGate();
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const sessionQueue = useLawyerSessionQueue();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ file: "" });
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [actionType, setActionType] = useState("");
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openHelp, setOpenHelp] = useState(false);

  const handleOpenHelp = () => setOpenHelp(true);
  const handleCloseHelp = () => setOpenHelp(false);

  const handleFileSelect = (file) => {
    if (file) {
      const validExtensions = ["xls", "xlsx"];
      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setErrors({
          ...errors,
          file: "Solo se permiten archivos .xls o .xlsx",
        });
        setSelectedFile(null);
        setForm({ ...form, file: "" });
        return;
      }

      setErrors({ ...errors, file: "" });
      setSelectedFile(file);
      setForm({ ...form, file: file.name });
    }
  };

  const handleConfirmDelete = () => {
    setOpenConfirm(false);
    doSubmit();
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("file", selectedFile);
    return formData;
  };

  const doSubmit = async () => {
    setLoading(true);
    try {
      const response =
        actionType === "upload"
          ? await createUploadLawsuit(buildFormData())
          : await deleteUploadLawsuit(buildFormData());

      showToast(response.data.message, "success");
      setForm({ file: "" });
      setSelectedFile(null);
      setErrors({});

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        showToast(error.response.data?.message || "Ocurrió un error.", "error");
      } else {
        console.log(error);
        showToast("Ocurrió un error inesperado.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setErrors({});

    if (!actionType) {
      showToast("Debe seleccionar una acción.", "info");
      return;
    }

    if (!selectedFile) {
      showToast("Debe seleccionar un archivo.", "info");
      return;
    }

    // "Eliminar Títulos" es local (JEX), no toca el portal judicial — no
    // necesita sesión. Solo "Cargar Títulos" (sube a SAC) sí.
    if (actionType !== "upload") {
      await doSubmit();
      return;
    }

    try {
      await sessionQueue.checkAndRun(
        () => previewUploadLawsuitLawyers(buildFormData()),
        doSubmit
      );
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        showToast(error.response.data?.message || "Ocurrió un error.", "error");
      } else {
        console.log(error);
        showToast("Ocurrió un error inesperado.", "error");
      }
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (actionType === "delete") {
      setOpenConfirm(true);
    } else {
      handleSubmit();
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={handleOpenHelp}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<AssignmentIcon />}
            title="Carga de Títulos (SAC) / Eliminación de Títulos (JEX)"
            description="Subir Títulos Electrónicos al sistema judicial (SAC) o eliminar títulos que no son necesarios en (JEX)."
          />

          <Box component="form" onSubmit={handleFormSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Acción</InputLabel>
                  <Select
                    value={actionType}
                    onChange={(e) => setActionType(e.target.value)}
                    label="Acción"
                  >
                    <MenuItem value="">SELECCIONAR UNA OPCION...</MenuItem>
                    <MenuItem value="upload">CARGAR TÍTULOS</MenuItem>
                    <MenuItem value="delete">ELIMINAR TÍTULOS</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FileUploadBox
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".xls,.xlsx"
                  placeholder="Seleccionar Archivo"
                  helperText="O arrastrá y soltá el archivo aquí (.xls, .xlsx)"
                />

                {errors.file && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.file}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <OnlineQueryTooltip show={!online} block>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    disabled={loading || !online || sessionQueue.isPending}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : actionType === "delete" ? (
                      "Eliminar Títulos"
                    ) : (
                      "Cargar Títulos"
                    )}
                  </Button>
                </OnlineQueryTooltip>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <ConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Confirmar eliminación"
        description="¿Estás seguro de que quieres eliminar estos títulos? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmColor="error"
        loading={loading}
      />

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={handleCloseHelp}
        title="Requisitos del archivo Excel"
        description="El archivo debe ser formato .xls o .xlsx."
        columns={["Códigos de Títulos"]}
        example={["Inm/1234/2025"]}
      />

      <LawyerSessionRequiredDialog {...sessionQueue.dialogProps} />
    </Grid>
  );
}
